/* iter-48e — Logline → Three-Act Synopsis generator.
 *
 * Sibling to ImportThreeActSynopsisDialog. Flow:
 *   1. Author types a 1-2 sentence logline (+ optional genre)
 *   2. Claude generates a 2,000-3,000 word three-act synopsis
 *   3. Author reviews & edits (live word-count meter)
 *   4. Author clicks "Import to Story Bible" — text is sent through
 *      the existing /import/three-act-synopsis endpoint (use_claude=true)
 *      so the same downstream pipeline (act split → bible → characters) runs.
 *
 * Why use the existing import endpoint at step 4 instead of writing a
 * second persist path? It guarantees the result lands in the story bible
 * identically to a file-upload import — single source of truth.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, Loader2, Wand2, ArrowRight, RotateCcw, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MIN_WORDS = 2000;
const MAX_WORDS = 3000;

function countWords(str) {
  if (!str) return 0;
  return (str.match(/\S+/g) || []).length;
}

export default function GenerateSynopsisFromLoglineDialog({ open, onClose }) {
  const { project, loadProject } = usePW();
  const [step, setStep]       = useState(1);
  const [logline, setLogline] = useState('');
  const [genre, setGenre]     = useState('');
  const [targetWords]         = useState(2200);  // tighter target to land in 2000-3000 band even on slow streams
  const [synopsis, setSynopsis] = useState('');
  const [busy, setBusy]       = useState(false);
  const [imported, setImported] = useState(null);
  const textRef = useRef(null);

  const wordCount = useMemo(() => countWords(synopsis), [synopsis]);
  const inRange   = wordCount >= MIN_WORDS && wordCount <= MAX_WORDS;

  const reset = () => {
    setStep(1); setLogline(''); setGenre(''); setSynopsis('');
    setBusy(false); setImported(null);
  };
  const closeAndReset = () => { reset(); onClose?.(); };

  // Step 1 → 2: generate (streams SSE so we beat the 60s ingress timeout).
  const generate = async () => {
    if (!project) { toast.error('Open a project first.'); return; }
    const trimmed = logline.trim();
    if (trimmed.length < 10) { toast.error('Logline must be at least 10 characters.'); return; }
    setBusy(true);
    setStep(2);
    setSynopsis('');
    try {
      const res = await fetch(
        `${API}/projects/${project.id}/generate/synopsis-from-logline`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify({
            logline: trimmed,
            genre: genre.trim() || null,
            target_words: targetWords,
          }),
        },
      );
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200) || res.statusText}`);
      }
      // SSE parser: events come as "event: NAME\ndata: JSON\n\n"
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let finalPayload = null;
      let errorMsg = null;
      // iter55g20 — Handle both \n\n and \r\n\r\n frame delimiters (some
      // sse-starlette deployments emit CRLFs). See auth_api.js for the
      // canonical helper used elsewhere.
      const findFrameEnd = (s) => {
        let best = -1, width = 0;
        for (const [d, w] of [['\r\n\r\n', 4], ['\n\n', 2], ['\r\r', 2]]) {
          const i = s.indexOf(d);
          if (i !== -1 && (best === -1 || i < best)) { best = i; width = w; }
        }
        return { idx: best, width };
      };
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        while (true) {
          const { idx, width } = findFrameEnd(buffer);
          if (idx === -1) break;
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + width);
          let eventName = 'message';
          let dataLine = '';
          for (const line of block.replace(/\r\n?/g, '\n').split('\n')) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
          }
          if (!dataLine) continue;
          let payload = {};
          try { payload = JSON.parse(dataLine); } catch { /* ignore */ }
          if (eventName === 'delta' && typeof payload.text === 'string') {
            accumulated += payload.text;
            setSynopsis(accumulated);  // live preview as Claude streams
          } else if (eventName === 'done') {
            finalPayload = payload;
          } else if (eventName === 'error') {
            errorMsg = payload.message || 'Generation failed';
          }
        }
      }
      if (errorMsg) throw new Error(errorMsg);
      // Cloudflare's preview ingress can close streams at ~60s before the
      // server emits a final `done` event. If we accumulated a usable amount
      // of prose with all three Act markers, treat the accumulated text as
      // the final synopsis — graceful degradation.
      let finalSynopsis = '';
      let finalWordCount = 0;
      let inRangeFinal = false;
      if (finalPayload) {
        finalSynopsis  = finalPayload.synopsis || accumulated;
        finalWordCount = finalPayload.word_count ?? countWords(finalSynopsis);
        inRangeFinal   = !!finalPayload.in_range;
      } else if (accumulated) {
        // Heuristic: must contain all three Act markers to be considered usable.
        const acts = (accumulated.match(/##\s*Act\s+(I{1,3}|IV)/gi) || []).length;
        if (acts < 3) {
          throw new Error(
            `Stream closed early without a complete three-act synopsis (got ${acts}/3 Acts, ${countWords(accumulated)} words). Try again or shorten the logline.`,
          );
        }
        finalSynopsis  = accumulated;
        finalWordCount = countWords(finalSynopsis);
        inRangeFinal   = finalWordCount >= MIN_WORDS && finalWordCount <= MAX_WORDS;
      } else {
        throw new Error('Stream closed without any content.');
      }
      setSynopsis(finalSynopsis);
      setStep(3);
      if (inRangeFinal) {
        toast.success(`Generated ${finalWordCount} words · ready to import`);
      } else if (finalWordCount < MIN_WORDS) {
        toast.warning(
          `Generated ${finalWordCount} words — add ${MIN_WORDS - finalWordCount} more before importing (preview ingress closes streams at 60s).`,
          { duration: 6000 },
        );
      } else {
        toast.warning(`Generated ${finalWordCount} words — trim to under ${MAX_WORDS} before importing.`, { duration: 6000 });
      }
    } catch (err) {
      const msg = err?.message || 'Unknown error';
      toast.error(`Generation failed: ${msg}`);
      setStep(1);
    } finally {
      setBusy(false);
    }
  };

  // Step 3 → 4: import (reuses the existing file-upload endpoint with a Blob)
  const importToBible = async () => {
    if (!project || !synopsis) return;
    if (!inRange) {
      toast.error(`Synopsis must be ${MIN_WORDS}-${MAX_WORDS} words. Currently ${wordCount}.`);
      return;
    }
    setBusy(true);
    try {
      const blob = new Blob([synopsis], { type: 'text/plain' });
      const fd = new FormData();
      fd.append('file', blob, 'generated-synopsis.txt');
      fd.append('use_claude', 'true');
      const { data } = await axios.post(
        `${API}/projects/${project.id}/import/three-act-synopsis`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000, withCredentials: true },
      );
      setImported(data);
      setStep(4);
      try { await loadProject?.(project.id, { silent: true }); } catch { /* ignore */ }
      toast.success(
        `Imported · ${data.characters_imported} character${data.characters_imported === 1 ? '' : 's'} added to the bible`,
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Import failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndReset(); }}>
      <DialogContent className="max-w-2xl" data-testid="logline-to-synopsis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" />
            Logline → Three-Act Synopsis
          </DialogTitle>
          <DialogDescription>
            Start with a single sentence. Claude expands it into a 2,500-word three-act synopsis you
            can edit before sending to the Story Bible.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — logline input */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="mb-1 inline-block text-[11.5px]">Logline (up to 10,000 characters — ~1,500 words)</Label>
              <Textarea
                data-testid="logline-input"
                value={logline}
                onChange={(e) => setLogline(e.target.value)}
                rows={6}
                placeholder="A cartographer&apos;s daughter must finish her dead father&apos;s atlas before the door he opened closes forever."
                maxLength={10000}
                className="resize-y border-border bg-background text-[13px] leading-relaxed"
              />
              {/* iter55g9 — live counter, red near the cap. */}
              <div
                className={
                  'mt-1 text-right text-[10.5px] ' +
                  (logline.length > 9500 ? 'text-destructive' : 'text-muted-foreground')
                }
                data-testid="logline-count"
              >
                {logline.length.toLocaleString()} / 10,000 characters
              </div>
            </div>
            <div>
              <Label className="mb-1 inline-block text-[11.5px]">Genre (optional)</Label>
              <Input
                data-testid="genre-input"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                placeholder="literary fantasy, cozy mystery, queer YA romance…"
                maxLength={80}
                className="border-border bg-background text-[13px]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={closeAndReset} data-testid="logline-cancel">Cancel</Button>
              <Button
                onClick={generate}
                disabled={logline.trim().length < 10 || busy}
                data-testid="logline-generate-btn"
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Generate synopsis
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — generating (live streaming preview) */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="text-[13px] font-medium" data-testid="logline-generating-status">
                  Drafting your three-act synopsis…
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Streaming live · usually 60-90 seconds for {targetWords} words.
                </p>
              </div>
              <span className="ml-auto rounded-sm border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10.5px] font-mono text-primary">
                {wordCount} words
              </span>
            </div>
            {synopsis && (
              <div
                data-testid="logline-streaming-preview"
                className="max-h-[300px] overflow-y-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11.5px] leading-relaxed text-foreground/80 scroll-thin"
              >
                {synopsis}
                <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-primary/60" />
              </div>
            )}
          </div>
        )}

        {/* Step 3 — review & edit */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[11.5px]">Draft synopsis · edit before importing</Label>
              <span
                data-testid="synopsis-word-count"
                className={[
                  'rounded-sm border px-2 py-0.5 text-[10.5px] font-mono',
                  inRange
                    ? 'border-primary/30 bg-primary/10 text-primary'
                    : 'border-destructive/40 bg-destructive/10 text-destructive',
                ].join(' ')}
              >
                {wordCount.toLocaleString()} / {MIN_WORDS.toLocaleString()}–{MAX_WORDS.toLocaleString()} words
              </span>
            </div>
            <Textarea
              ref={textRef}
              data-testid="synopsis-review-textarea"
              value={synopsis}
              onChange={(e) => setSynopsis(e.target.value)}
              rows={16}
              className="resize-none border-border bg-background font-mono text-[12px] leading-relaxed"
            />
            {!inRange && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-[11.5px] text-destructive">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Synopsis must be {MIN_WORDS}-{MAX_WORDS} words to import. Trim or expand the draft, then try again.
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <Button
                variant="ghost"
                onClick={() => { setStep(1); }}
                disabled={busy}
                data-testid="logline-regenerate-btn"
                className="gap-2"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Start over
              </Button>
              <Button
                onClick={importToBible}
                disabled={!inRange || busy}
                data-testid="logline-import-btn"
                className="gap-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Import to Story Bible
              </Button>
            </div>
          </div>
        )}

        {/* Step 4 — done */}
        {step === 4 && imported && (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="logline-done">
            <div className="rounded-full bg-primary/10 p-3">
              <Check className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-base font-semibold">Imported to your Story Bible.</h3>
            <p className="text-[12.5px] text-muted-foreground">
              {imported.characters_imported || 0} character{imported.characters_imported === 1 ? '' : 's'} added · {imported.word_count} words · 3 acts.
            </p>
            <Button onClick={closeAndReset} className="mt-2" data-testid="logline-done-close">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
